import './index.less';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Message, Tooltip } from '@meitu/saas-ui';
import IconFont from '@meitu/xdesign-icon-font';
import {
    BaseOperateView,
    BaseStagePlugin,
    // useActiveFrameId,
    useActiveFrameIndex,
    useFramework,
    useIsLongImageMode,
    // useStageList,
    // useStageListLength,
} from '@/framework';
import { useLocaleStore } from '@/i18n/index';
import { useCropImgState } from '@/layers/image-layer/plugins/apps/user-editor/crop-img/states/crop-img-state';
import { checkPosterTypeIsAiPPT } from '@/utils/stage-kit';
import { IExportPlugin, ILayerProps, MAX_STAGES, MAX_STAGES_TIP, PluginType } from '@/version-types';
import { AdjustOperate } from './adjust-operate';
import { QuickStageHistory } from './history/quick-stage-history';
import { QuickAdjustOperate } from './operate/quick-adjust-operate';
import { QuickResizeOperate } from './operate/quick-resize-operate';
import { QuickStageOperate } from './operate/quick-stage-operate';
import { ResizeOperate } from './resize-operate';
import { StageOperateImpl } from './stage-operate';

const StageOperateView: React.FC = React.memo(() => {
    const framework = useFramework();
    const activeIndex = useActiveFrameIndex();
    const isCropImage = useCropImgState()((state) => state.isCropImage);
    const isLongImageMode = useIsLongImageMode();
    const { $t } = useLocaleStore();
    const { frame, editor, edgelessCanvas, events } = framework.service;
    const layoutRefreshRafRef = useRef<number | null>(null);
    const waitStageReadyRafRef = useRef<number | null>(null);
    const waitStageReadyCountRef = useRef(0);
    const [, refreshLayout] = useState(0);

    const scheduleLayoutRefresh = useCallback(() => {
        if (layoutRefreshRafRef.current !== null) return;
        layoutRefreshRafRef.current = window.requestAnimationFrame(() => {
            layoutRefreshRafRef.current = null;
            refreshLayout((prev) => prev + 1);
        });
    }, []);

    const mouseEvents = isLongImageMode
        ? {
              onMouseEnter: () => {
                  editor.__emitMouseEnter(frame.getActiveFrameId());
              },
              onMouseLeave: () => {
                  editor.__emitMouseLeave(frame.getActiveFrameId());
              },
          }
        : {};

    useEffect(() => {
        scheduleLayoutRefresh();
        return () => {
            if (layoutRefreshRafRef.current !== null) {
                window.cancelAnimationFrame(layoutRefreshRafRef.current);
                layoutRefreshRafRef.current = null;
            }
            if (waitStageReadyRafRef.current !== null) {
                window.cancelAnimationFrame(waitStageReadyRafRef.current);
                waitStageReadyRafRef.current = null;
            }
        };
    }, [scheduleLayoutRefresh]);

    useEffect(() => {
        const handler = frame.onBaseOperate(() => {
            frame.setPickActiveFrame(false);
        });
        return () => events.off(handler);
    }, []);

    const [count, setCount] = useState(frame.getFrameIdsLength());
    useEffect(() => {
        const handler = frame.onRerenderAll(() => {
            setCount(frame.getFrameIdsLength());
            waitStageReadyCountRef.current = 0;
            scheduleLayoutRefresh();
        });
        return () => events.off(handler);
    }, [scheduleLayoutRefresh]);

    const [_, refresh] = useState(0);
    const [hiddenAll, setHiddenAll] = useState(edgelessCanvas.getHiddenAll());
    useEffect(() => {
        const handlers = [
            frame.onResize(() => {
                refresh((prev) => prev + 1);
                waitStageReadyCountRef.current = 0;
                scheduleLayoutRefresh();
            }),
            frame.onRerenderAll(() => {
                refresh((prev) => prev + 1);
                waitStageReadyCountRef.current = 0;
                scheduleLayoutRefresh();
            }),
            edgelessCanvas.onHiddenAllChange(() => {
                setHiddenAll(edgelessCanvas.getHiddenAll());
                waitStageReadyCountRef.current = 0;
                scheduleLayoutRefresh();
            }),
        ];
        return () => {
            handlers.forEach((handler) => events.off(handler));
        };
    }, [scheduleLayoutRefresh]);

    useEffect(() => {
        waitStageReadyCountRef.current = 0;
        scheduleLayoutRefresh();
    }, [activeIndex, scheduleLayoutRefresh]);

    const activeFrameId = count > 0 ? frame.indexToFrameId(activeIndex) : null;
    const activeStageShape = activeFrameId !== null ? frame.getShape(activeFrameId) : null;
    const isActiveStageReady =
        activeFrameId !== null &&
        activeStageShape !== null &&
        !!frame.get(activeFrameId) &&
        activeStageShape.width > 0 &&
        activeStageShape.height > 0;

    useEffect(() => {
        if (count <= 0 || hiddenAll) return;
        if (isActiveStageReady) {
            waitStageReadyCountRef.current = 0;
            return;
        }
        if (waitStageReadyCountRef.current >= 60 || waitStageReadyRafRef.current !== null) return;
        waitStageReadyCountRef.current += 1;
        waitStageReadyRafRef.current = window.requestAnimationFrame(() => {
            waitStageReadyRafRef.current = null;
            scheduleLayoutRefresh();
        });
    }, [isActiveStageReady, activeIndex, count, hiddenAll, scheduleLayoutRefresh]);

    if (isCropImage || count <= 0 || hiddenAll || !isActiveStageReady || !activeStageShape) return null;

    const disableUp = activeIndex === 0;
    const disableDown = activeIndex === count - 1;
    const disableDel = count === 1;
    const canAddFrame = () => {
        if (!framework.service.edgelessCanvas.isEdgelessMode) {
            return framework.service.frame.getFrameIdsLength() < MAX_STAGES;
        }

        const activeFrameId = framework.service.frame.getActiveFrameId();
        if (!framework.service.edgelessPPT.isPPTFrame(activeFrameId)) return true;

        const defaultTaskId = framework.service.agentLayout.getEnable()
            ? framework.service.agentLayout.getDefaultPPTTaskId()
            : undefined;
        const taskId = framework.service.edgelessPPT.getPPTFrameGroupTaskId(activeFrameId, defaultTaskId);

        return framework.service.edgelessPPT.canAddPPTFrames(1, taskId, defaultTaskId);
    };

    return (
        <BaseStagePlugin name="stage-operate" zIndex={5}>
            <BaseOperateView layer={activeStageShape}>
                {/* 舞台调整尺寸 */}
                {isLongImageMode ? <ResizeOperate /> : <AdjustOperate />}
                {/* 右边快速操作 */}
                <div className="c-quick-operate" {...mouseEvents}>
                    <Tooltip
                        placement="right"
                        title={
                            !isLongImageMode ? $t((v) => v.stageManager.prePage) : $t((v) => v.stageManager.moveUpPage)
                        }
                        overlayClassName="c-quick-operate-tooltip"
                    >
                        <div
                            className={classNames('c-quick-operate__item', { disable: disableUp })}
                            onClick={() => {
                                framework.service.registration
                                    .getInstance<QuickStageOperate>('QuickStageOperate')
                                    .up()
                                    ?.emitQuickClick('up_canvas');
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <IconFont name={!isLongImageMode ? 'ArrowUpBold' : 'SortUpBold'} fontSize={16} />
                        </div>
                    </Tooltip>

                    <Tooltip
                        placement="right"
                        title={
                            !isLongImageMode
                                ? $t((v) => v.stageManager.nextPage)
                                : $t((v) => v.stageManager.moveDownPage)
                        }
                        overlayClassName="c-quick-operate-tooltip"
                    >
                        <div
                            className={classNames('c-quick-operate__item', {
                                disable: disableDown,
                            })}
                            onClick={() => {
                                framework.service.registration
                                    .getInstance<QuickStageOperate>('QuickStageOperate')
                                    .down()
                                    ?.emitQuickClick('down_canvas');
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <IconFont name={!isLongImageMode ? 'ArrowDownBold' : 'SortDownBold'} fontSize={16} />
                        </div>
                    </Tooltip>
                    <Tooltip
                        placement="right"
                        title={$t((v) => v.stageManager.addPage)}
                        overlayClassName="c-quick-operate-tooltip"
                    >
                        <div
                            className={classNames('c-quick-operate__item')}
                            onClick={async () => {
                                if (canAddFrame()) {
                                    const bgLayer = framework.service.frame.getBackgroundLayer(
                                        framework.service.frame.getActiveFrameId(),
                                    );
                                    const isAiPPT = checkPosterTypeIsAiPPT(framework.service.editor.getPosterType());
                                    const op = await framework.service.registration
                                        .getInstance<QuickStageOperate>('QuickStageOperate')
                                        .create(
                                            framework.service.frame.getActiveIndex(),
                                            undefined,
                                            isAiPPT ? (bgLayer ? bgLayer : undefined) : undefined,
                                            framework.service.customData.getCustomData<ILayerProps[]>('masterPage'),
                                        );
                                    framework.service.frame.scrollToActiveFrame();
                                    op.setStageSelection().emitQuickClick('new_canvas');
                                } else {
                                    Message.warning(MAX_STAGES_TIP());
                                }
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <IconFont name="PlusCircleBold" fontSize={16} />
                        </div>
                    </Tooltip>
                    <Tooltip
                        placement="right"
                        title={$t((v) => v.stageManager.copyPage)}
                        overlayClassName="c-quick-operate-tooltip"
                    >
                        <div
                            className={classNames('c-quick-operate__item')}
                            onClick={async () => {
                                if (canAddFrame()) {
                                    const op = await framework.service.registration
                                        .getInstance<QuickStageOperate>('QuickStageOperate')
                                        .clone(framework.service.frame.getActiveIndex());
                                    framework.service.frame.scrollToActiveFrame();
                                    op.setStageSelection().emitQuickClick('copy_canvas');
                                } else {
                                    Message.warning(MAX_STAGES_TIP());
                                }
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <IconFont name="DuplicateBold" fontSize={16} />
                        </div>
                    </Tooltip>
                    <Tooltip
                        placement="right"
                        title={$t((v) => v.stageManager.delPage)}
                        overlayClassName="c-quick-operate-tooltip"
                    >
                        <div
                            className={classNames('c-quick-operate__item', { disable: disableDel })}
                            onClick={() => {
                                const length = framework.service.frame.getFrameIdsLength();
                                if (length > 1) {
                                    framework.service.registration
                                        .getInstance<QuickStageOperate>('QuickStageOperate')
                                        .emitQuickClick('delete_canvas')
                                        .delete(framework.service.frame.getActiveIndex())
                                        .setStageSelection();
                                    framework.service.frame.scrollToActiveFrame();
                                }
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <IconFont name="TrashCanBold" fontSize={16} />
                        </div>
                    </Tooltip>
                </div>
            </BaseOperateView>
        </BaseStagePlugin>
    );
});

const exportPlugin: IExportPlugin = {
    type: PluginType.STAGE,
    sort: 3,
    name: 'stage-operate',
    createInstance({ identity }) {
        return [
            { key: 'StageOperate', value: new StageOperateImpl(identity) },
            { key: 'QuickStageOperate', value: new QuickStageOperate(identity) },
            { key: 'QuickAdjustOperate', value: new QuickAdjustOperate(identity) },
            { key: 'QuickResizeOperate', value: new QuickResizeOperate(identity) },
        ];
    },
    createHistory({ identity }) {
        return [new QuickStageHistory(identity)];
    },
    render() {
        return <StageOperateView />;
    },
};

export default exportPlugin;
